import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CharacterData } from '../../types/character';
import { EnhancedCharacter } from '../../utils/ClassComponentRegistry';
import { calculateTierFromLevel } from '../../utils/levelTierMapping';
import { getClassOptions, findClassById } from '../../utils/classDataLoader';
import Input from '../Common/Input';
import EnhancedSelect from '../Common/EnhancedSelect';
import Button from '../Common/Button';
import ProficiencyDisplay from './ProficiencyDisplay';
import './BasicInfo.css';

interface BasicInfoProps {
  enhancedCharacter: EnhancedCharacter;
  updateCharacterData: (updates: any) => void;
  disableAutoSave?: () => void;
  enableAutoSave?: () => void;
  onLevelUp?: () => void;
}

const BasicInfo: React.FC<BasicInfoProps> = ({ 
  enhancedCharacter, 
  updateCharacterData, 
  disableAutoSave, 
  enableAutoSave, 
  onLevelUp 
}) => {
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editState, setEditState] = useState<{
    class: string;
    heritage: string;
    subclass: string;
    community: string;
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Data loading states
  const [classOptions, setClassOptions] = useState<Array<{value: string, label: string}>>([]);
  const [heritageOptions, setHeritageOptions] = useState<Array<{value: string, label: string}>>([]);
  const [communityOptions, setCommunityOptions] = useState<Array<{value: string, label: string}>>([]);
  const [subclassOptions, setSubclassOptions] = useState<Array<{value: string, label: string}>>([]);
  const [communityData, setCommunityData] = useState<Array<{id: string, name: string}>>([]);

  // Load data options
  useEffect(() => {
    const loadOptions = async () => {
      try {
        // Load class options using IDs as values (to match character creation)
        const classOptionsData = await getClassOptions();
        setClassOptions(
          classOptionsData.map(c => ({
            value: c.id,
            label: c.name
          }))
        );

        // Load heritage options from ancestries.json
        const ancestriesData = await import('../../data/ancestries.json');
        const ancestries = ancestriesData.default || ancestriesData;
        if (Array.isArray(ancestries)) {
          setHeritageOptions(
            ancestries.map((ancestry: any) => ({
              value: ancestry.id,
              label: ancestry.name
            }))
          );
        }

        // Load community options from communities.json
        const communitiesData = await import('../../data/communities.json');
        const communities = communitiesData.default?.communities || communitiesData.communities;
        if (Array.isArray(communities)) {
          // Store community data for ID-to-name conversion
          setCommunityData(
            communities.map((community: any) => ({
              id: community.id,
              name: community.name
            }))
          );
          
          // Set dropdown options using IDs (to match how character creation works)
          setCommunityOptions(
            communities.map((community: any) => ({
              value: community.id,
              label: community.name
            }))
          );
        }
      } catch (error) {
        console.error('Failed to load dropdown options:', error);
      }
    };

    loadOptions();
  }, []);

  // Load subclass options when class changes
  useEffect(() => {
    const loadSubclassOptions = async () => {
      if (!editState?.class) {
        setSubclassOptions([]);
        return;
      }

      try {
        const classData = await findClassById(editState.class);
        if (classData?.subclasses) {
          const subclassOpts = classData.subclasses.map((sub: any) => ({
            value: sub.id,
            label: sub.name
          }));
          setSubclassOptions(subclassOpts);
          
          // Auto-select first subclass if current subclass is empty
          if (!editState.subclass && subclassOpts.length > 0) {
            setEditState(prev => ({
              ...prev!,
              subclass: subclassOpts[0].value
            }));
          }
        }
      } catch (error) {
        console.error('Failed to load subclass options:', error);
        setSubclassOptions([]);
      }
    };

    if (isEditMode) {
      loadSubclassOptions();
    }
  }, [isEditMode, editState?.class]);

  if (!enhancedCharacter.character) {
    return (
      <div className="basic-info">
        <div className="basic-info__loading">
          Loading character information...
        </div>
      </div>
    );
  }

  const character = enhancedCharacter.character;

  // Helper function to capitalize first letter
  const capitalizeFirstLetter = (str: string): string => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Helper function to get community name from ID
  const getCommunityName = (communityId: string): string => {
    if (!communityId) return '';
    const community = communityData.find(c => c.id === communityId);
    return community ? community.name : communityId;
  };

  // Helper function to get class name from ID
  const getClassName = (classId: string): string => {
    if (!classId) return '';
    const classOption = classOptions.find(c => c.value === classId);
    return classOption ? classOption.label : classId;
  };

  // Helper function to get subclass name from ID
  const getSubclassName = (subclassId: string): string => {
    if (!subclassId) return '';
    const subclassOption = subclassOptions.find(s => s.value === subclassId);
    if (subclassOption) {
      return subclassOption.label;
    }
    // Fallback: Convert kebab-case ID to proper title case
    const lowercaseWords = ['of', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'with', 'by', 'from'];
    const words = subclassId.split('-');
    return words
      .map((word, index) => {
        const lowerWord = word.toLowerCase();
        // Always capitalize first and last words, or if not in lowercase list
        if (index === 0 || index === words.length - 1 || !lowercaseWords.includes(lowerWord)) {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }
        return lowerWord;
      })
      .join(' ');
  };

  // Helper function to get heritage name from ID
  const getHeritageName = (heritageId: string): string => {
    if (!heritageId) return '';
    const heritageOption = heritageOptions.find(h => h.value === heritageId);
    return heritageOption ? heritageOption.label : heritageId;
  };

  const handleInputChange = (field: keyof CharacterData) => (value: string) => {
    // Only allow name changes in read-only mode
    if (!isEditMode && field !== 'name') {
      return;
    }
    updateCharacterData({ [field]: value });
  };

  const handleEditStateChange = (field: string, value: string) => {
    setEditState(prev => {
      const newState = { ...prev!, [field]: value };
      
      // Reset subclass when class changes and auto-select first subclass
      if (field === 'class') {
        newState.subclass = ''; // Will be updated when subclass options load
      }
      
      return newState;
    });
  };

  const handleEnterEditMode = () => {
    setIsEditMode(true);
    setEditState({
      class: character.class || '',
      heritage: character.heritage || '',
      subclass: character.subclass || '',
      community: character.community || ''
    });
    setErrorMessage(null);
    if (disableAutoSave) disableAutoSave();
  };

  const handleSaveChanges = async () => {
    try {
      if (editState) {
        updateCharacterData(editState);
      }
      setIsEditMode(false);
      setEditState(null);
      setErrorMessage(null);
      if (enableAutoSave) enableAutoSave();
    } catch (error) {
      setErrorMessage('Error saving changes. Please try again.');
      console.error('Failed to save character changes:', error);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditState(null);
    setErrorMessage(null);
    if (enableAutoSave) enableAutoSave();
  };

  const handleLevelUp = () => {
    if (onLevelUp) {
      onLevelUp();
    }
  };

  // Calculate tier from level
  const characterLevel = parseInt(character.level) || 1;
  const characterTier = calculateTierFromLevel(characterLevel);

  return (
    <div className="basic-info">
      {/* Error message */}
      {errorMessage && (
        <div className="basic-info__error" role="alert">
          {errorMessage}
        </div>
      )}


      <div className="basic-info__name">
        <Input
          value={character.name}
          onChange={handleInputChange('name')}
          placeholder="Character Name"
          label="Character Name"
          className="basic-info__name-input"
        />
      </div>
      
      <div className="basic-info__stats">
        {/* Level display */}
        <div className="input-group">
          <label className="input-label">Level</label>
          <div className="basic-info__tier-value">{character.level}</div>
        </div>

        <div className="input-group">
          <label className="input-label">Tier</label>
          <div className="basic-info__tier-value">{characterTier}</div>
        </div>

        {/* Protected fields - animated transitions between display and edit modes */}
        <AnimatePresence mode="wait">
          {!isEditMode ? (
            <>
              <motion.div
                className="input-group"
                key="class-display"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ delay: 0.05 }}
              >
                <label className="input-label">Class</label>
                <div className="basic-info__protected-display">
                  {character.class ? getClassName(character.class) : 'None'}
                </div>
              </motion.div>

              <motion.div
                className="input-group"
                key="heritage-display"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ delay: 0.1 }}
              >
                <label className="input-label">Heritage</label>
                <div className="basic-info__protected-display">
                  {character.heritage ? getHeritageName(character.heritage) : 'None'}
                </div>
              </motion.div>

              <motion.div
                className="input-group"
                key="subclass-display"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ delay: 0.15 }}
              >
                <label className="input-label">Subclass</label>
                <div className="basic-info__protected-display">
                  {character.subclass ? getSubclassName(character.subclass) : 'None'}
                </div>
              </motion.div>

              <motion.div
                className="input-group"
                key="community-display"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ delay: 0.2 }}
              >
                <label className="input-label">Community</label>
                <div className="basic-info__protected-display">
                  {character.community ? getCommunityName(character.community) : 'None'}
                </div>
              </motion.div>
            </>
          ) : (
            <>
              <motion.div
                key="class-edit"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: 0.05 }}
              >
                <EnhancedSelect
                  value={editState?.class || ''}
                  onChange={(value) => handleEditStateChange('class', value)}
                  options={classOptions}
                  label="Class"
                  placeholder="Search classes..."
                  isSearchable={true}
                  menuPlacement="auto"
                />
              </motion.div>

              <motion.div
                key="heritage-edit"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: 0.1 }}
              >
                <EnhancedSelect
                  value={editState?.heritage || ''}
                  onChange={(value) => handleEditStateChange('heritage', value)}
                  options={heritageOptions}
                  label="Heritage"
                  placeholder="Search heritages..."
                  isSearchable={true}
                  menuPlacement="auto"
                />
              </motion.div>

              <motion.div
                key="subclass-edit"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: 0.15 }}
              >
                <EnhancedSelect
                  value={editState?.subclass || ''}
                  onChange={(value) => handleEditStateChange('subclass', value)}
                  options={subclassOptions}
                  label="Subclass"
                  placeholder="Search subclasses..."
                  disabled={!editState?.class || subclassOptions.length === 0}
                  isSearchable={true}
                  menuPlacement="auto"
                />
              </motion.div>

              <motion.div
                key="community-edit"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: 0.2 }}
              >
                <EnhancedSelect
                  value={editState?.community || ''}
                  onChange={(value) => handleEditStateChange('community', value)}
                  options={communityOptions}
                  label="Community"
                  placeholder="Search communities..."
                  isSearchable={true}
                  menuPlacement="auto"
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Proficiency Display */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{ gridColumn: '1 / -1', marginTop: 'var(--semantic-spacing-component-md)' }}
        >
          <ProficiencyDisplay character={character} />
        </motion.div>
      </div>

      {/* Action buttons - bottom right */}
      <div className="basic-info__actions">
        {!isEditMode && (
          <Button
            variant="PRIMARY"
            size="SMALL"
            onClick={handleLevelUp}
            disabled={characterLevel >= 10}
            className="basic-info__level-up"
          >
            Level Up
          </Button>
        )}
        
        {!isEditMode ? (
          <Button 
            variant="SECONDARY" 
            size="SMALL"
            onClick={handleEnterEditMode}
          >
            Edit
          </Button>
        ) : (
          <div className="basic-info__edit-controls">
            <Button 
              variant="PRIMARY" 
              size="SMALL"
              onClick={handleSaveChanges}
            >
              Save
            </Button>
            <Button 
              variant="SECONDARY" 
              size="SMALL"
              onClick={handleCancelEdit}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* Enhanced character computed stats display */}
      {enhancedCharacter.computedStats && (
        <div className="basic-info__computed-stats">
          <h4>Computed Stats</h4>
          <div className="basic-info__computed-stats-grid">
            <div className="basic-info__stat">
              <span className="basic-info__stat-label">Evasion:</span>
              <span className="basic-info__stat-value">{enhancedCharacter.computedStats.evasion}</span>
            </div>
            <div className="basic-info__stat">
              <span className="basic-info__stat-label">Armor:</span>
              <span className="basic-info__stat-value">{enhancedCharacter.computedStats.armor}</span>
            </div>
            <div className="basic-info__stat">
              <span className="basic-info__stat-label">HP Slots:</span>
              <span className="basic-info__stat-value">{enhancedCharacter.computedStats.hpSlots}</span>
            </div>
            <div className="basic-info__stat">
              <span className="basic-info__stat-label">Hope:</span>
              <span className="basic-info__stat-value">{enhancedCharacter.computedStats.hope}</span>
            </div>
            <div className="basic-info__stat">
              <span className="basic-info__stat-label">Major Threshold:</span>
              <span className="basic-info__stat-value">{enhancedCharacter.computedStats.thresholds.major}</span>
            </div>
            <div className="basic-info__stat">
              <span className="basic-info__stat-label">Severe Threshold:</span>
              <span className="basic-info__stat-value">{enhancedCharacter.computedStats.thresholds.severe}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BasicInfo;